import type {
	AiDraftSimulationMessage,
	AiDraftSimulationResult,
	DepartmentSummary,
	TenantMemberSummary,
} from '@livechat/shared';
import { type FormEvent, useEffect, useState } from 'react';

import { listDepartments, listMembers, simulateTenantAiDraft } from '../api.js';

interface SimulationTurn extends AiDraftSimulationMessage {
	result?: AiDraftSimulationResult;
}

function score(value: number): string {
	return value.toFixed(2);
}

export function TenantAiSimulator({
	disabled = false,
	tenantId,
}: {
	disabled?: boolean;
	tenantId: string;
}) {
	const [agents, setAgents] = useState<TenantMemberSummary[]>([]);
	const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
	const [agentUserId, setAgentUserId] = useState('');
	const [departmentId, setDepartmentId] = useState('');
	const [transcript, setTranscript] = useState<SimulationTurn[]>([]);
	const [priorClarifications, setPriorClarifications] = useState(0);
	const [draft, setDraft] = useState('');
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		void Promise.all([listMembers(tenantId), listDepartments(tenantId)])
			.then(([members, loadedDepartments]) => {
				if (!current) return;
				const activeAgents = members.filter(
					(member) => member.memberType === 'ai_agent' && member.status === 'active',
				);
				setAgents(activeAgents);
				setDepartments(
					loadedDepartments.filter((department) => department.status === 'active'),
				);
				setAgentUserId((selected) => selected || activeAgents[0]?.id || '');
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load the Draft simulator',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [tenantId]);

	function clearTranscript(): void {
		setTranscript([]);
		setPriorClarifications(0);
		setError(undefined);
	}

	async function run(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		const message = draft.trim();
		if (!message || !agentUserId || busy || disabled) return;
		setBusy(true);
		setError(undefined);
		try {
			const result = await simulateTenantAiDraft(tenantId, {
				agentUserId,
				...(departmentId ? { departmentId } : {}),
				history: transcript.slice(-12).map(({ message: value, role }) => ({
					message: value,
					role,
				})),
				message,
				priorGuardrailClarifications: priorClarifications,
			});
			setTranscript((current) => [
				...current,
				{ message, role: 'visitor' },
				{ message: result.message, result, role: 'agent' },
			]);
			if (result.guardrailAction === 'clarify') {
				setPriorClarifications((current) => Math.min(3, current + 1));
			}
			setDraft('');
		} catch (caught) {
			setError(
				caught instanceof Error ? caught.message : 'Could not run the Draft simulation',
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="settings-card integration-panel ai-simulator-panel">
			<div className="integration-panel-heading ai-simulator-heading">
				<div>
					<h4 className="panel-title">Draft simulator</h4>
					<p>
						Test the current Draft persona, knowledge, model and guardrails before
						publishing. Simulations are not saved or sent to visitors.
					</p>
				</div>
				<span className="ui-badge ui-badge-info">Draft only</span>
			</div>

			{error && <div className="alert error">{error}</div>}
			{disabled && (
				<div className="ai-simulator-warning">
					Save the AI settings above before testing those changes.
				</div>
			)}

			{loading ? (
				<div className="empty-configuration">Loading simulator…</div>
			) : !agents.length ? (
				<div className="empty-configuration">
					Create and activate an AI team member before running a simulation.
				</div>
			) : (
				<>
					<div className="ai-simulator-controls">
						<label>
							<span>AI agent</span>
							<select
								disabled={busy}
								onChange={(event) => {
									setAgentUserId(event.currentTarget.value);
									clearTranscript();
								}}
								value={agentUserId}
							>
								{agents.map((agent) => (
									<option key={agent.id} value={agent.id}>
										{agent.displayName}
									</option>
								))}
							</select>
						</label>
						<label>
							<span>Department context</span>
							<select
								disabled={busy}
								onChange={(event) => {
									setDepartmentId(event.currentTarget.value);
									clearTranscript();
								}}
								value={departmentId}
							>
								<option value="">No department</option>
								{departments.map((department) => (
									<option key={department.id} value={department.id}>
										{department.name}
									</option>
								))}
							</select>
						</label>
						<button
							className="secondary-button"
							disabled={busy || !transcript.length}
							onClick={clearTranscript}
							type="button"
						>
							Clear conversation
						</button>
					</div>

					<div aria-live="polite" className="ai-simulator-transcript">
						{transcript.length ? (
							transcript.map((turn, index) => (
								<div className={`ai-simulator-turn ${turn.role}`} key={index}>
									<div className="ai-simulator-bubble">
										<span>
											{turn.role === 'visitor' ? 'Visitor' : 'AI agent'}
										</span>
										<p>{turn.message}</p>
										{turn.result && (
											<small>
												{turn.result.handoff
													? 'Would hand off'
													: turn.result.guardrailAction === 'clarify'
														? 'Would ask for detail'
														: 'Would answer'}
												{' · '}
												{turn.result.model} · {turn.result.reasoningEffort}{' '}
												· {turn.result.durationMs} ms
											</small>
										)}
									</div>
									{turn.result &&
										(turn.result.sources.length ||
											turn.result.topRelevanceScore !== undefined ||
											turn.result.guardrailCode) && (
											<details className="ai-simulator-evidence">
												<summary>Execution details</summary>
												{turn.result.guardrailCode && (
													<p>
														Guardrail:{' '}
														{turn.result.guardrailCode.replaceAll(
															'_',
															' ',
														)}
													</p>
												)}
												{turn.result.topRelevanceScore !== undefined && (
													<p>
														Top evidence score:{' '}
														{score(turn.result.topRelevanceScore)}
													</p>
												)}
												{!!turn.result.sources.length && (
													<ul>
														{turn.result.sources.map((source) => (
															<li
																key={`${source.sourceId}-${source.rank}`}
															>
																{source.url ? (
																	<a
																		href={source.url}
																		rel="noreferrer"
																		target="_blank"
																	>
																		{source.title}
																	</a>
																) : (
																	source.title
																)}
																<span>
																	{score(source.relevanceScore)}
																	{source.used ? ' · cited' : ''}
																</span>
															</li>
														))}
													</ul>
												)}
											</details>
										)}
								</div>
							))
						) : (
							<div className="ai-simulator-empty">
								Enter a visitor message to see how the current Draft would respond.
							</div>
						)}
					</div>

					<form className="ai-simulator-composer" onSubmit={(event) => void run(event)}>
						<label>
							<span>Visitor message</span>
							<textarea
								disabled={busy || disabled}
								maxLength={4_000}
								onChange={(event) => setDraft(event.currentTarget.value)}
								placeholder="Ask the AI agent a test question"
								rows={3}
								value={draft}
							/>
						</label>
						<button
							className="primary-button"
							disabled={busy || disabled || !draft.trim() || !agentUserId}
						>
							{busy ? 'Running…' : 'Run Draft'}
						</button>
					</form>
				</>
			)}
		</section>
	);
}
